import { z } from "zod";
import { getAddress } from "@bbb/utils";
import { Id, NonEmpty, Url } from "./base";
import { envValues } from "./constant_values";
//#region Schema
const EnvValuesSchema = z.enum(envValues);
const UtcIsoDateTime = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/, "Must be an ISO 8601 UTC timestamp ending in Z")
    .refine((value) => Number.isFinite(Date.parse(value)), "Must be a valid date");
export const EnvSchema = z.object({
    NODE_ENV: EnvValuesSchema.default("development"),
    PORT: NonEmpty.regex(/^\d+$/).default("8080"),
    POSTMARK_SERVER_TOKEN: z.string().min(1, "POSTMARK_SERVER_TOKEN is required"),
    FIREBASE_PROJECT_ID: Id.optional(),
    FIREBASE_CLIENT_EMAIL: z.email().optional(),
    FIREBASE_PRIVATE_KEY: z.string().optional(),
    FIREBASE_CREDENTIAL_SOURCE: z
        .enum(["auto", "application_default", "service_account"])
        .default("auto"),
    FIRESTORE_COLLECTION_ALLOWLIST: z
        .string()
        .default("dashboardSensitiveOperations")
        .transform((raw) => raw
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)),
    DOTENV_DISABLE: z.string().optional(),
    DOTENV_CONFIG_PATH: z.string().optional(),
    CORS_ALLOWED_ORIGINS: z
        .string()
        .optional()
        .transform((raw) => raw
        ?.split(",")
        .map((value) => value.trim())
        .filter(Boolean) ?? []),
    DIDIT_API_KEY: z.string(),
    DIDIT_BASE_URL: Url.default("https://verification.didit.me"),
    POST_KYC_CALLBACK_URL: Url,
    DIDIT_WORKFLOW_ID: Id,
    DIDIT_WEBHOOK_SECRET: Id,
    DASHBOARD_APP_URL: Url.optional(),
    REDIS_URL: z.string().url().optional(),
    REDIS_KEY_PREFIX: z.string().default("bbb:dashboard"),
    OFFLINE_MODE: z
        .enum(["true", "false"])
        .default("false")
        .transform((v) => v === "true"),
    STORAGE_MODE: z.enum(["cloud", "mock"]).default("cloud"),
    MOCK_STORAGE_BASE_URL: Url.optional(),
    FEEDBACK_MEDIA_BUCKET: z.string().optional(),
    FEEDBACK_MEDIA_BASE_URL: Url.optional(),
    X_CLIENT_ID: z.string().min(1, "X_CLIENT_ID is required"),
    X_CLIENT_SECRET: z.string().min(1, "X_CLIENT_SECRET is required"),
    X_CALLBACK_URI: z.string().url("X_CALLBACK_URI must be a valid URL"),
    DISCORD_CLIENT_ID: z.string().min(1, "DISCORD_CLIENT_ID is required"),
    DISCORD_CLIENT_SECRET: z.string().min(1, "DISCORD_CLIENT_SECRET is required"),
    DISCORD_CALLBACK_URI: z.string().url("DISCORD_CALLBACK_URI must be a valid URL"),
    RECAPTCHA_SECRET_KEY: z.string().min(1, "RECAPTCHA_SECRET_KEY is required"),
    RECAPTCHA_MIN_SCORE: z.coerce.number().min(0).max(1).default(0.5),
    GNOSIS_API_KEY: z.string().optional(),
    CAMPAIGN_NFT_SIGNER_PRIVATE_KEY: z
        .string()
        .regex(/^0x[0-9a-fA-F]{64}$/, "CAMPAIGN_NFT_SIGNER_PRIVATE_KEY must be a 32-byte hex private key")
        .optional(),
    GOOGLE_FEEDBACK_WEBHOOK_URL: z
        .string()
        .url("GOOGLE_FEEDBACK_WEBHOOK_URL must be a valid URL")
        .optional(),
    GOOGLE_FEEDBACK_WEBHOOK_TOKEN: z.string().optional(),
    POSTHOG_PROJECT_API_KEY: z.string().optional(),
    POSTHOG_HOST: Url.default("https://eu.i.posthog.com"),
    PLATFORM_METRICS_IP_HASH_SALT: z.string().optional(),
    EARLY_ADOPTER_KYC_WINDOW_START: UtcIsoDateTime.optional(),
    EARLY_ADOPTER_KYC_WINDOW_END: UtcIsoDateTime.optional(),
    LEADERBOARD_TASKS_QUEUE_PATH: z.string().optional(),
    LEADERBOARD_TASKS_HANDLER_URL: Url.optional(),
    LEADERBOARD_TASKS_AUTH_AUDIENCE: Url.optional(),
    LEADERBOARD_TASKS_SERVICE_ACCOUNT_EMAIL: z.email().optional(),
    LEADERBOARD_TASKS_ALLOWED_SERVICE_ACCOUNTS: z
        .string()
        .optional()
        .transform((raw) => raw
        ?.split(",")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean) ?? []),
    LEADERBOARD_TASKS_AUTH_TOKEN: z.string().optional(),
    CAMPAIGN_INSIGHTS_TASKS_QUEUE_PATH: z.string().optional(),
    CAMPAIGN_INSIGHTS_TASKS_HANDLER_URL: Url.optional(),
    CAMPAIGN_INSIGHTS_TASKS_AUTH_AUDIENCE: Url.optional(),
    CAMPAIGN_INSIGHTS_TASKS_SERVICE_ACCOUNT_EMAIL: z.email().optional(),
    CAMPAIGN_INSIGHTS_TASKS_ALLOWED_SERVICE_ACCOUNTS: z
        .string()
        .optional()
        .transform((raw) => raw
        ?.split(",")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean) ?? []),
    CAMPAIGN_FINALIZATION_TASKS_QUEUE_PATH: z.string().optional(),
    CAMPAIGN_FINALIZATION_TASKS_HANDLER_URL: Url.optional(),
    CAMPAIGN_FINALIZATION_TASKS_AUTH_AUDIENCE: Url.optional(),
    CAMPAIGN_FINALIZATION_TASKS_SERVICE_ACCOUNT_EMAIL: z.email().optional(),
    CAMPAIGN_FINALIZATION_TASKS_ALLOWED_SERVICE_ACCOUNTS: z
        .string()
        .optional()
        .transform((raw) => raw
        ?.split(",")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean) ?? []),
    // Cloud Scheduler task auth settings
    // The audience must be the Cloud Run service URL the scheduler hits.
    SCHEDULER_TASKS_AUTH_AUDIENCE: Url,
    // Comma-separated service account emails allowed to call scheduler task routes.
    // Must include at least one entry to avoid silent allow-all behavior.
    SCHEDULER_TASKS_ALLOWED_SERVICE_ACCOUNTS: z
        .string()
        .min(1, "SCHEDULER_TASKS_ALLOWED_SERVICE_ACCOUNTS is required")
        .transform((raw) => raw
        .split(",")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean))
        .refine((list) => list.length > 0, "SCHEDULER_TASKS_ALLOWED_SERVICE_ACCOUNTS must contain at least one service account email"),
    DEPLOY_SMOKE_AUTH_AUDIENCE: Url.optional(),
    DEPLOY_SMOKE_ALLOWED_SERVICE_ACCOUNTS: z
        .string()
        .optional()
        .transform((raw) => raw
        ?.split(",")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean) ?? []),
    LEADERBOARD_TASKS_FORCE_SYNC: z
        .enum(["true", "false"])
        .default("false")
        .transform((v) => v === "true"),
    NOTIFICATION_DISPATCH_ALLOWED_EMAILS: z
        .string()
        .optional()
        .transform((raw) => raw
        ?.split(",")
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean) ?? []),
    PLATFORM_ADMIN_WALLET_ALLOWLIST: z
        .string()
        .optional()
        .transform((raw, ctx) => {
        const normalized = [];
        for (const rawAddress of raw
            ?.split(",")
            .map((value) => value.trim())
            .filter(Boolean) ?? []) {
            try {
                normalized.push(getAddress(rawAddress));
            }
            catch {
                ctx.addIssue({
                    code: "custom",
                    message: "PLATFORM_ADMIN_WALLET_ALLOWLIST must contain valid EVM addresses",
                });
            }
        }
        return normalized;
    }),
    ESCROW_OWNERS_ADDRESS: z
        .string()
        .transform((val) => val
        .split(",")
        .map((addr) => addr.trim())
        .filter(Boolean))
        .pipe(z.array(z
        .string()
        .superRefine((addr, ctx) => {
        try {
            getAddress(addr);
        }
        catch {
            ctx.addIssue({
                code: "custom",
                message: "ESCROW_OWNERS_ADDRESS must be a valid EVM address",
            });
        }
    })
        .transform((addr) => getAddress(addr)))),
    ESCROW_THRESHOLD: z.string(),
    ETHEREUM_MAINNET_RPC_URL: z.string().url().optional(),
});
//#endregion Type
